/**
 * A simple in-memory storage solution for managing application state.
 * This module provides methods to get and set various types of data,
 * as well as to manage drawer state and theme preferences using localStorage.
 */

export const setIdsOnEndpoint = (dataType, data, storage) => {
  const { endpointKey, items, reportId = '-1', subId = '-1' } = data

  if (!storage.data[dataType][endpointKey]) {
    storage.data[dataType][endpointKey] = { [reportId]: { [subId]: Object.freeze(items) } }
  } else if (!storage.data[dataType][endpointKey][reportId]) {
    storage.data[dataType][endpointKey][reportId] = { [subId]: Object.freeze(items) }
  } else {
    storage.data[dataType][endpointKey][reportId][subId] = Object.freeze(items)
  }
}

export const setAllKeyOnEndpoint = (dataType, data, storage) => {
  const { endpointKey, items } = data

  if (!storage.data[dataType][endpointKey]) {
    storage.data[dataType][endpointKey] = { all: Object.freeze(items) }
  } else {
    storage.data[dataType][endpointKey].all = Object.freeze(items)
  }

  return storage.data[dataType][endpointKey].all
}

// If reportId is present, we are dealing with a report collection that can have multiple IDs.
// Otherwise, it's a single collection identified by 'all'.

/**
 * Sets the report collection in the storage.
 * @param {string} dataType
 * @param {*} data
 * @param {Object} storage
 * @returns {*} The stored report collection.
 */
export const setReportCollection = (dataType, data, storage) => {
  const { reportId } = data

  if (reportId) {
    return setIdsOnEndpoint(dataType, data, storage)
  }

  return setAllKeyOnEndpoint(dataType, data, storage)
}

const Storage = {
  data: {
    ITEMS_EXCLUDED_BY_PREFILTER: {},

    ITEMS: {}, // PREFILTERED
    ITEMS_MERGED: {},
    GROUPS_MERGED: {},
    GROUPS_MERGED_FILTERED: {},
    GROUPS_MERGED_FILTERED_SORTED: {},

    LINEITEMS: [],
    ORDERS: [],
    PROJECTS: [],
  },

  get(dataType) {
    return this.data[dataType]
  },
  set(dataType, data) {
    const { endpointKey, moduleName } = data
    const isSupportingCollection = endpointKey !== moduleName

    return isSupportingCollection
      ? setAllKeyOnEndpoint(dataType, data, Storage)
      : setReportCollection(dataType, data, Storage)
  },

  getStoredState(storageKey) {
    return window.localStorage.getItem(storageKey)
  },
  getStoredJson(storageKey) {
    const storedState = this.getStoredState(storageKey)

    if (!storedState) return null

    try {
      return JSON.parse(storedState)
    } catch {
      return storedState
    }
  },
  validateStoredDrawerState(storedState) {
    return ['false', 'true'].indexOf(storedState) !== -1
  },
  getDrawerState(storageKey) {
    const storedState = this.getStoredState(storageKey)
    const validStoredState = this.validateStoredDrawerState(storedState)

    return validStoredState ? storedState : 'true'
  },
  getNavCollapsedState(drawerState) {
    return drawerState === 'true'
  },
  getTheme(storageKey) {
    return this.getStoredState(storageKey)
  },
}

export default Storage
